import React, { useCallback, useEffect, useMemo, useState } from "react";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { useLoader } from "../../../context/LoaderContext";
import { useUserData } from "../../../hooks/useUserData";
import { resolvePlatformSuperAdminStaffId } from "../../../utils/permissionUtils";
import {
  fetchSponsorCroStudyAccessEmailTemplates,
  saveSponsorCroStudyAccessEmailTemplate,
} from "../../../services/superAdminOrgService";
import CapaActionPlanningEmailConfigEditor from "../capa/CapaActionPlanningEmailConfigEditor";
import {
  SPONSOR_CRO_STUDY_ACCESS_EMAIL_MERGE_TAGS,
  SPONSOR_CRO_STUDY_ACCESS_TEMPLATE_META,
} from "./sponsorCroStudyAccessEmailTags";
import "../capa/CapaActionPlanningEmailConfigEditor.scss";
import "./CapaConfigMaster.scss";
import "./SponsorCroStudyAccessEmailTemplatesPanel.scss";

function emptyDrafts() {
  const next = {};
  SPONSOR_CRO_STUDY_ACCESS_TEMPLATE_META.forEach((meta) => {
    next[meta.key] = { subjectTemplate: "", bodyHtmlTemplate: "" };
  });
  return next;
}

/**
 * QMS/CAPA-style side nav cards + email editor.
 * Hosted inside CtmsWorkspaceModal (full-width) from Customer Organisations.
 */
export default function SponsorCroStudyAccessEmailTemplatesPanel({
  organizationId,
  orgName = "",
}) {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [activeSection, setActiveSection] = useState(
    SPONSOR_CRO_STUDY_ACCESS_TEMPLATE_META[0]?.key || "StudyAccessAssigned",
  );
  const [drafts, setDrafts] = useState(emptyDrafts);
  const [savedSnapshot, setSavedSnapshot] = useState(emptyDrafts);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const hasUnsavedChanges = useMemo(() => {
    return SPONSOR_CRO_STUDY_ACCESS_TEMPLATE_META.some((meta) => {
      const draft = drafts[meta.key] || {};
      const saved = savedSnapshot[meta.key] || {};
      return (
        String(draft.subjectTemplate || "") !==
          String(saved.subjectTemplate || "") ||
        String(draft.bodyHtmlTemplate || "") !==
          String(saved.bodyHtmlTemplate || "")
      );
    });
  }, [drafts, savedSnapshot]);

  const loadTemplates = useCallback(
    async ({ showBlockingLoader = true } = {}) => {
      if (!organizationId) return;
      if (showBlockingLoader) {
        showLoader();
        setLoading(true);
      }
      try {
        const list = await fetchSponsorCroStudyAccessEmailTemplates(
          organizationId,
        );
        const next = emptyDrafts();
        (list || []).forEach((item) => {
          if (!item?.templateKey || !next[item.templateKey]) return;
          next[item.templateKey] = {
            subjectTemplate: item.subjectTemplate || "",
            bodyHtmlTemplate: item.bodyHtmlTemplate || "",
          };
        });
        setDrafts(next);
        setSavedSnapshot(next);
      } catch (error) {
        console.error(error);
        showAlert({
          title: "Error",
          message:
            error?.message ||
            "Could not load study access email templates.",
          type: "error",
        });
      } finally {
        if (showBlockingLoader) {
          setLoading(false);
          hideLoader();
        }
      }
    },
    [organizationId, showLoader, hideLoader, showAlert],
  );

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const updateDraft = useCallback((key, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { subjectTemplate: "", bodyHtmlTemplate: "" }),
        ...patch,
      },
    }));
  }, []);

  const switchSection = useCallback(
    (key) => {
      if (key === activeSection) return;
      if (hasUnsavedChanges) {
        const ok = window.confirm(
          "You have unsaved changes. Switch section without saving?",
        );
        if (!ok) return;
      }
      setActiveSection(key);
    },
    [activeSection, hasUnsavedChanges],
  );

  const handleSaveActive = useCallback(async () => {
    const meta = SPONSOR_CRO_STUDY_ACCESS_TEMPLATE_META.find(
      (m) => m.key === activeSection,
    );
    if (!meta || !organizationId) return;

    const draft = drafts[meta.key] || {};
    const subject = String(draft.subjectTemplate || "").trim();
    const body = String(draft.bodyHtmlTemplate || "").trim();
    if (!subject || !body) {
      showAlert({
        title: "Required",
        message: `Please enter subject and body for “${meta.title}”.`,
        type: "warning",
      });
      return;
    }

    setSaving(true);
    showLoader();
    try {
      await saveSponsorCroStudyAccessEmailTemplate({
        organizationId,
        templateKey: meta.key,
        subjectTemplate: subject,
        bodyHtmlTemplate: body,
        updatedBy: resolvePlatformSuperAdminStaffId(userData),
      });
      const saved = { subjectTemplate: subject, bodyHtmlTemplate: body };
      setSavedSnapshot((prev) => ({ ...prev, [meta.key]: saved }));
      setDrafts((prev) => ({ ...prev, [meta.key]: saved }));
      showAlert({
        title: "Saved",
        message: `${meta.title} template saved.`,
        type: "success",
      });
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Error",
        message: error?.message || "Could not save template.",
        type: "error",
      });
    } finally {
      setSaving(false);
      hideLoader();
    }
  }, [
    activeSection,
    drafts,
    organizationId,
    userData,
    showAlert,
    showLoader,
    hideLoader,
  ]);

  const activeMeta =
    SPONSOR_CRO_STUDY_ACCESS_TEMPLATE_META.find(
      (m) => m.key === activeSection,
    ) || SPONSOR_CRO_STUDY_ACCESS_TEMPLATE_META[0];
  const activeDraft = drafts[activeMeta?.key] || {
    subjectTemplate: "",
    bodyHtmlTemplate: "",
  };

  return (
    <div className="capa-config-saas sponsor-cro-email-templates-panel">
      <InfoAlert
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        onClose={hideAlert}
      />

      {orgName ? (
        <p className="text-muted small mb-2 px-1 flex-shrink-0">
          Organisation: <strong>{orgName}</strong>
        </p>
      ) : null}

      {loading ? (
        <div className="card shadow-sm">
          <div className="card-body text-muted">
            Loading study access email templates…
          </div>
        </div>
      ) : (
        <div className="row g-3 capa-config-saas__layout sponsor-cro-email-templates-panel__layout">
          <div className="col-md-4 col-xl-3 capa-config-saas__nav-col">
            <div className="card capa-config-saas__nav-card">
              <div className="card-header fw-semibold">
                Configuration sections
              </div>
              <div className="list-group list-group-flush capa-config-saas__nav-list">
                {SPONSOR_CRO_STUDY_ACCESS_TEMPLATE_META.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    className={`list-group-item list-group-item-action capa-config-saas__nav-item ${
                      activeSection === section.key ? "active" : ""
                    }`}
                    onClick={() => switchSection(section.key)}
                    aria-current={
                      activeSection === section.key ? "page" : undefined
                    }
                  >
                    <div className="d-flex align-items-start gap-2">
                      <i
                        className={`${section.icon || "fas fa-envelope"} mt-1`}
                        aria-hidden="true"
                      />
                      <div>
                        <div className="fw-semibold capa-config-saas__nav-item-title">
                          {section.title}
                        </div>
                        <div
                          className={`small capa-config-saas__nav-item-subtitle ${
                            activeSection === section.key
                              ? "text-white-50"
                              : "text-muted"
                          }`}
                        >
                          {section.subtitle}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-8 col-xl-9 capa-config-saas__content-col">
            <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
              <div className="card-body capa-ap-email-panel-body">
                {hasUnsavedChanges ? (
                  <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                    <i className="fas fa-exclamation-triangle me-2" />
                    You have unsaved changes. Click <strong>Save</strong> to
                    apply updates.
                  </div>
                ) : null}

                <h6 className="mb-2 flex-shrink-0">{activeMeta?.title}</h6>

                <div className="capa-ap-email-editor-grow">
                  <CapaActionPlanningEmailConfigEditor
                    subjectTemplate={activeDraft.subjectTemplate}
                    onSubjectChange={(value) =>
                      updateDraft(activeMeta.key, {
                        subjectTemplate: value,
                      })
                    }
                    bodyTemplate={activeDraft.bodyHtmlTemplate}
                    onBodyChange={(value) =>
                      updateDraft(activeMeta.key, {
                        bodyHtmlTemplate: value,
                      })
                    }
                    subjectDisabled={saving}
                    bodyDisabled={saving}
                    description={activeMeta?.description}
                    mergeTags={SPONSOR_CRO_STUDY_ACCESS_EMAIL_MERGE_TAGS}
                    fillAvailableHeight
                    blockImages
                  />
                </div>

                <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveActive}
                    disabled={saving || loading}
                  >
                    {saving ? "Saving..." : "Save email templates"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
